/**
 * FeatureGuard Component
 *
 * Conditionally renders components based on institution type feature availability.
 * Provides both HOC and component-based approaches for feature gating.
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { selectAvailableFeatures, selectInstitutionType } from '../../redux/slices/institutionSlice';

/**
 * Higher-Order Component (HOC) to wrap components with feature guard
 *
 * @param {string} featurePath - Dot-separated feature path (e.g., "workflows.student_health_form")
 * @returns {Function} HOC that wraps a component
 *
 * @example
 * const ProtectedComponent = withFeatureGuard('workflows.pharmacy_inventory')(PharmacyComponent);
 */
export const withFeatureGuard = (featurePath) => (Component) => {
  return (props) => {
    const availableFeatures = useSelector(selectAvailableFeatures);
    const institutionType = useSelector(selectInstitutionType);

    // Parse feature path (e.g., "workflows.student_health_form")
    const [category, featureName] = featurePath.split('.');

    // Check if feature is available
    // Features not in config = common features = always available
    const isAvailable = availableFeatures[category]?.includes(featureName) ?? true;

    if (!isAvailable) {
      return null; // Feature not available for this institution type
    }

    return <Component {...props} />;
  };
};

/**
 * Component-based Feature Guard
 *
 * Wraps children and conditionally renders based on feature availability.
 *
 * @param {Object} props
 * @param {string} props.feature - Dot-separated feature path (e.g., "workflows.student_health_form")
 * @param {React.ReactNode} props.children - Children to render if feature is available
 * @param {React.ReactNode} props.fallback - Optional fallback to render if feature is unavailable
 *
 * @example
 * <FeatureGuard feature="workflows.pharmacy_inventory">
 *   <PharmacyInventoryComponent />
 * </FeatureGuard>
 */
export const FeatureGuard = ({ feature, children, fallback = null }) => {
  const availableFeatures = useSelector(selectAvailableFeatures);
  const institutionType = useSelector(selectInstitutionType);

  // If no feature specified, render children (common feature)
  if (!feature) {
    return children;
  }

  // Parse feature path
  const [category, featureName] = feature.split('.');

  // Check if feature is available
  // Features not in config = common features = always available
  const isAvailable = availableFeatures[category]?.includes(featureName) ?? true;

  return isAvailable ? children : fallback;
};

/**
 * Hook to check if a feature is available
 *
 * @param {string} featurePath - Dot-separated feature path
 * @returns {boolean} True if feature is available for current institution type
 *
 * @example
 * const isPharmacyAvailable = useFeatureAvailable('workflows.pharmacy_inventory');
 */
export const useFeatureAvailable = (featurePath) => {
  const availableFeatures = useSelector(selectAvailableFeatures);
  const institutionType = useSelector(selectInstitutionType);

  if (!featurePath) {
    return true; // No feature path = common feature
  }

  const [category, featureName] = featurePath.split('.');

  // Features not in config = common features = always available
  return availableFeatures[category]?.includes(featureName) ?? true;
};

export default FeatureGuard;
